/**
 * UI 테마 설정
 * 폴더 스타일 푸른색 테마
 */
export const UI_THEME = {
  // === 메인 패널 색상 ===
  panel: {
    // 배경 그라데이션
    background:
      "linear-gradient(180deg, rgba(45, 85, 130, 0.95) 0%, rgba(35, 70, 110, 0.98) 100%)",
    // 테두리
    border: "1px solid rgba(100, 160, 220, 0.4)",
    // 블러 효과
    backdropFilter: "blur(12px)",
    // 그림자
    boxShadow: "0 10px 40px rgba(0, 50, 100, 0.4)",
  },

  // === 폴더 탭 색상 ===
  folderTab: {
    // 기본 배경
    background:
      "linear-gradient(180deg, rgba(55, 100, 145, 0.95) 0%, rgba(45, 85, 125, 0.98) 100%)",
    // 호버 배경
    backgroundHover:
      "linear-gradient(180deg, rgba(70, 120, 165, 0.95) 0%, rgba(55, 100, 145, 0.98) 100%)",
    // 테두리
    border: "1px solid rgba(100, 160, 220, 0.4)",
    // 너비
    width: "18px",
    widthSmall: "14px",
    // 높이
    height: "24px",
    heightSmall: "18px",
  },

  // === 타이포그래피 ===
  typography: {
    // 제목 (Heading)
    heading: {
      fontFamily: "'Pretendard GOV Variable', 'Space Mono', monospace",
      h1: {
        fontSize: "20px",
        fontWeight: "700",
        letterSpacing: "0.1em",
        lineHeight: "1.3",
      },
      h2: {
        fontSize: "16px",
        fontWeight: "700",
        letterSpacing: "0.05em",
        lineHeight: "1.4",
      },
      h3: {
        fontSize: "14px",
        fontWeight: "600",
        letterSpacing: "0.03em",
        lineHeight: "1.4",
      },
    },
    // 본문 (Body)
    body: {
      fontFamily: "'Pretendard GOV Variable', 'Black Han Sans', monospace",
      large: {
        fontSize: "15px",
        fontWeight: "400",
        letterSpacing: "0.01em",
        lineHeight: "1.6",
      },
      medium: {
        fontSize: "13px",
        fontWeight: "400",
        letterSpacing: "0.01em",
        lineHeight: "1.5",
      },
      small: {
        fontSize: "11px",
        fontWeight: "400",
        letterSpacing: "0.01em",
        lineHeight: "1.5",
      },
    },
    // 캡션/라벨
    caption: {
      fontFamily: "'Pretendard GOV Variable', 'Space Mono', monospace",
      fontSize: "10px",
      fontWeight: "400",
      letterSpacing: "0.05em",
      lineHeight: "1.4",
    },
  },

  // === 텍스트 색상 ===
  text: {
    primary: "rgba(255, 255, 255, 0.95)",
    secondary: "rgba(180, 210, 240, 0.85)",
    muted: "rgba(180, 210, 240, 0.6)",
    accent: "rgba(220, 240, 255, 0.9)",
  },

  // === 버튼 색상 ===
  button: {
    // 기본 버튼
    default: {
      background: "rgba(255, 255, 255, 0.15)",
      border: "1px solid rgba(100, 160, 220, 0.4)",
      color: "rgba(220, 240, 255, 0.9)",
    },
    // 활성화된 버튼
    active: {
      background: "rgba(70, 130, 180, 0.5)",
      border: "1px solid rgba(100, 160, 220, 0.4)",
      color: "rgba(220, 240, 255, 0.95)",
    },
    // 주요 액션 버튼
    primary: {
      background: "linear-gradient(135deg, #4488bb 0%, #3377aa 100%)",
      color: "rgba(255, 255, 255, 0.95)",
    },
    // 비활성화된 버튼
    disabled: {
      background: "rgba(80, 120, 160, 0.3)",
      color: "rgba(180, 210, 240, 0.4)",
    },
    // 성공/저장 버튼
    success: {
      background: "rgba(70, 160, 100, 0.4)",
      color: "rgba(200, 255, 200, 0.95)",
    },
    // 위험/삭제 버튼
    danger: {
      color: "rgba(255, 150, 150, 0.85)",
      colorHover: "rgba(255, 200, 200, 0.95)",
    },
  },

  // === 입력 필드 ===
  input: {
    background: "rgba(20, 50, 80, 0.5)",
    border: "1px solid rgba(100, 160, 220, 0.4)",
    color: "rgba(255, 255, 255, 0.95)",
  },

  // === 축 색상 (X축 / Z축) ===
  axis: {
    x: {
      background: "rgba(220, 100, 100, 0.3)",
      border: "1px solid rgba(220, 100, 100, 0.5)",
      color: "rgba(255, 200, 200, 0.95)",
      tabBackground:
        "linear-gradient(180deg, rgba(145, 85, 85, 0.95) 0%, rgba(125, 70, 70, 0.98) 100%)",
    },
    z: {
      background: "rgba(100, 160, 220, 0.4)",
      border: "1px solid rgba(100, 180, 240, 0.6)",
      color: "rgba(200, 230, 255, 0.95)",
      tabBackground:
        "linear-gradient(180deg, rgba(55, 100, 145, 0.95) 0%, rgba(45, 85, 125, 0.98) 100%)",
    },
  },

  // === 선택된 항목 ===
  selected: {
    background: "rgba(80, 140, 200, 0.4)",
  },

  // === Border Radius ===
  borderRadius: {
    panel: "8px",
    panelSmall: "6px",
    button: "6px",
    input: "4px",
  },

  // === 모달/오버레이 ===
  overlay: {
    background: "rgba(0, 20, 40, 0.8)",
  },
} as const;

// 헬퍼 함수: 폴더 패널 래퍼 스타일
export const getFolderPanelStyle = () => ({
  background: UI_THEME.panel.background,
  border: UI_THEME.panel.border,
  backdropFilter: UI_THEME.panel.backdropFilter,
});

// 헬퍼 함수: 폴더 탭 스타일 (상단 탭용 - 상단에만 border-radius)
export const getFolderTabStyle = (
  position: "top" | "left" | "right" = "top",
  size: "default" | "small" = "default"
) => {
  const baseStyle = {
    background: UI_THEME.folderTab.background,
  };

  if (position === "top") {
    return {
      ...baseStyle,
      height:
        size === "small"
          ? UI_THEME.folderTab.heightSmall
          : UI_THEME.folderTab.height,
      borderRadius: `${UI_THEME.borderRadius.panel} ${UI_THEME.borderRadius.panel} 0 0`,
      borderTop: UI_THEME.folderTab.border,
      borderLeft: UI_THEME.folderTab.border,
      borderRight: UI_THEME.folderTab.border,
      borderBottom: "none",
    };
  } else if (position === "left") {
    return {
      ...baseStyle,
      width:
        size === "small"
          ? UI_THEME.folderTab.widthSmall
          : UI_THEME.folderTab.width,
      borderRadius: `${UI_THEME.borderRadius.panel} 0 0 ${UI_THEME.borderRadius.panel}`,
      borderTop: UI_THEME.folderTab.border,
      borderBottom: UI_THEME.folderTab.border,
      borderLeft: UI_THEME.folderTab.border,
    };
  } else {
    return {
      ...baseStyle,
      width:
        size === "small"
          ? UI_THEME.folderTab.widthSmall
          : UI_THEME.folderTab.width,
      borderRadius: `0 ${UI_THEME.borderRadius.panel} ${UI_THEME.borderRadius.panel} 0`,
      borderTop: UI_THEME.folderTab.border,
      borderBottom: UI_THEME.folderTab.border,
      borderRight: UI_THEME.folderTab.border,
    };
  }
};

// 헬퍼 함수: 타이포그래피 스타일 가져오기
export const getTypography = (
  type: "heading" | "body" | "caption",
  level?: "h1" | "h2" | "h3" | "large" | "medium" | "small"
) => {
  if (type === "heading" && level) {
    const heading = UI_THEME.typography.heading;
    const levelStyle = heading[level as "h1" | "h2" | "h3"];
    return {
      fontFamily: heading.fontFamily,
      ...levelStyle,
    };
  } else if (type === "body" && level) {
    const body = UI_THEME.typography.body;
    const levelStyle = body[level as "large" | "medium" | "small"];
    return {
      fontFamily: body.fontFamily,
      ...levelStyle,
    };
  } else if (type === "caption") {
    return UI_THEME.typography.caption;
  }
  return {};
};
